import {
  type DragEventHandler,
  type KeyboardEventHandler,
  type MouseEventHandler,
  useEffect,
  useState,
} from 'react'

interface DragState {
  group: symbol | null
  index: number
}

/**
 * There is one drag at a time, so a module-level record is enough to know
 * which row is being dragged across rows.
 */
const dragState: DragState = { group: null, index: -1 }

/**
 * Options for {@link useSortableItem}.
 */
export interface SortableItemOptions {
  /**
   * Rows only reorder among rows of the same group, so each list on screen
   * needs its own, even lists of the same kind.
   */
  group: symbol
  index: number
  /**
   * Moves the row. `toIndex` can be past either end of the list when the row
   * is moved with the keyboard, so it is clamped by the callee.
   */
  onMove: (fromIndex: number, toIndex: number) => unknown
  isDisabled?: boolean
}

/**
 * Drag-to-reorder for a row. The row only becomes draggable while its handle
 * is pressed, so text inside it stays selectable. With the handle focused,
 * the up and down arrows move the row one place.
 *
 * @param options
 */
export function useSortableItem({
  group,
  index,
  onMove,
  isDisabled,
}: SortableItemOptions) {
  const [isArmed, setIsArmed] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  // The press can end anywhere, not only on the handle, and a row left armed
  // would drag from anywhere in it.
  useEffect(() => {
    if (!isArmed) {
      return
    }
    const disarm = () => setIsArmed(false)
    window.addEventListener('mouseup', disarm)
    return () => window.removeEventListener('mouseup', disarm)
  }, [isArmed])

  const onDragStart: DragEventHandler<HTMLElement> = (e) => {
    dragState.group = group
    dragState.index = index
    e.dataTransfer.effectAllowed = 'move'
    // Firefox only starts a drag with data.
    e.dataTransfer.setData('text/plain', '')
    setIsDragging(true)
  }

  const onDragEnd: DragEventHandler<HTMLElement> = () => {
    dragState.group = null
    dragState.index = -1
    setIsDragging(false)
    setIsArmed(false)
  }

  const onDragOver: DragEventHandler<HTMLElement> = (e) => {
    if (dragState.group !== group) {
      return
    }
    e.preventDefault()
    const from = dragState.index
    if (from === index) {
      return
    }
    // Only swaps once the pointer passes the row's middle, so rows of
    // different heights don't flicker back and forth.
    const rect = e.currentTarget.getBoundingClientRect()
    const middle = rect.top + rect.height / 2
    if (
      (from < index && e.clientY < middle) ||
      (from > index && e.clientY > middle)
    ) {
      return
    }
    onMove(from, index)
    dragState.index = index
  }

  const onHandleMouseDown: MouseEventHandler<HTMLElement> = () => {
    if (!isDisabled) {
      setIsArmed(true)
    }
  }

  const onHandleKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
    if (isDisabled || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) {
      return
    }
    e.preventDefault()
    const handle = e.currentTarget
    onMove(index, e.key === 'ArrowUp' ? index - 1 : index + 1)
    // React moves the row's element to its new place, which can drop the
    // focus.
    requestAnimationFrame(() => handle.focus())
  }

  return {
    rowProps: {
      draggable: isArmed && !isDisabled,
      onDragStart,
      onDragEnd,
      onDragOver,
    },
    handleProps: {
      onMouseDown: onHandleMouseDown,
      onKeyDown: onHandleKeyDown,
    },
    isDragging,
  }
}
